import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Typography, Button, List, ListItem } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';

import { newTabLink } from 'utils';
import { ArchivalUnitReference, NewOrder } from 'types';
import { PageKey, UrlRoute } from 'enums';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import { AuthCtx } from 'components/auth/auth-ctx';
import { OrderCtx } from 'components/order-ctx/order-ctx';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { TextField } from 'components/text-field/text-field';
import { useInitialValues } from 'components/table/table-hook';

import { postNewOrder } from './order-api';
import { validationSchema } from './order-validation';
import { OrderAcknowledgeDialog } from './order-acknowledge-dialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    form: {
      color: 'black',
      marginBottom: 50,
    },
    link: {
      color: theme.palette.primary.main,
      marginRight: 15,
      textTransform: 'none',
    },
    unitLink: {
      width: '100%',
      color: theme.palette.primary.main,
      display: 'flex',
    },
    listItem: {
      padding: '5px 0px',
    },
    listItemName: {
      display: 'flex',
      justifyContent: 'flex-start',
      flexGrow: 1,
      textTransform: 'none',
    },
    iconButton: {
      width: 20,
      height: 20,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '& svg': {
        fontSize: '1rem',
      },
    },
    submitButton: {
      margin: '15px 0',
      marginRight: '15px',
    },
  })
);

function NOOP() {}

export function OrderNew() {
  const classes = useStyles();

  const history = useHistory();
  const [newOrderId, setNewOrderId] = useState<string | undefined>();
  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const { userInfo, userSheet, researchSheetToDisplayInOrder } = useContext(
    AuthCtx
  );
  const { showNotification } = useContext(NotificationCtx);
  const {
    aisOrder,
    archivalUnits,
    handleRemoveUnit,
    clearContext,
  } = useContext(OrderCtx);
  const { locale, formatMessage } = useIntl();

  const { currentlyUsedSheetId } = userInfo ?? {};

  const today = format(new Date(), 'yyyy-MM-dd');

  const initialValues: NewOrder = useInitialValues(
    aisOrder,
    (archivalUnits ?? []).map((r) => r.archivalUnitReference),
    userInfo,
    userSheet
  );

  const handleRemoveItem = useCallback(
    (props: FormikProps<NewOrder>, unit: ArchivalUnitReference) => (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      const { values, setFieldValue } = props;
      const { archivalUnits } = values;

      e.preventDefault();
      e.stopPropagation();

      handleRemoveUnit?.(unit);

      setFieldValue(
        'archivalUnits',
        archivalUnits?.filter(({ id }) => id !== unit?.id)
      );
    },
    [handleRemoveUnit]
  );

  const handleRemoveAisObject = useCallback(
    (props: FormikProps<NewOrder>) => () => {
      const { setFieldValue } = props;

      setFieldValue('archivalAidMetadata', undefined);
      setFieldValue('archivalCollection', undefined);
      clearContext?.();
    },
    [clearContext]
  );

  const handleSubmit = useCallback(
    async (values: NewOrder, actions: FormikHelpers<NewOrder>) => {
      try {
        const processedData = !!values.archivalUnits?.length
          ? {
              ...values,
              archivalUnits: values.archivalUnits,
            }
          : values;
        const response = await postNewOrder(processedData);

        if (response) {
          showNotification(
            formatMessage({ id: 'aisOrder.successfullySent' }),
            NotificationVariant.SUCCESS
          );

          clearContext?.();
          actions.resetForm();

          if (!!userSheet) {
            setNewOrderId(response.id);
          } else {
            history.push('/portfolio/historie-objednavek');
          }
        } else {
          throw new Error('Status not ok');
        }
      } catch (e) {
        showNotification(
          formatMessage({ id: 'aisOrder.failedToSend' }),
          NotificationVariant.ERROR
        );
      }

      actions.setSubmitting(false);
    },
    [clearContext, formatMessage, history, showNotification, userSheet]
  );

  useEffect(() => {
    setBreadcrumbs([
      ...(userSheet?.id
        ? initialBreadcrumbs.userSheetBreadCrumbs
        : initialBreadcrumbs.breadcrumbs),
      {
        title: formatMessage({ id: 'aisOrder.newOrder' }),
        key: PageKey.PORTFOLIO_ORDER_NEW,
      },
    ]);
  }, [formatMessage, locale, setBreadcrumbs, userSheet]);

  return (
    <Grid container className={classes.root}>
      {!!currentlyUsedSheetId ? (
        <Grid item xs={10} md={6}>
          <div className={classes.form}>
            <Formik<NewOrder>
              initialValues={initialValues}
              enableReinitialize={true}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount={false}
              validationSchema={validationSchema}
            >
              {(props) => {
                const { handleSubmit, values } = props;
                const orderVersion = !!values?.archivalUnits?.length
                  ? 'archival-units'
                  : !!values?.archivalAidMetadata
                  ? 'archival-aid'
                  : !!values?.archivalCollection
                  ? 'archival-collection'
                  : 'generic';

                const showGeneric = orderVersion === 'generic';
                const showAid = orderVersion === 'archival-aid';
                const showCollection = orderVersion === 'archival-collection';
                const showUnits = orderVersion === 'archival-units';

                return (
                  <form onSubmit={handleSubmit}>
                    <FormItemTextField
                      name="fullName"
                      label={formatMessage({ id: 'aisOrder.fullName' })}
                      disabled={true}
                    />
                    <TextField
                      name="currentlyUsedSheetOrdinalNumber"
                      label={formatMessage({
                        id: 'aisOrder.currentlyUsedSheetOrdinalNumber',
                      })}
                      value={researchSheetToDisplayInOrder ?? ''}
                      onChange={NOOP}
                      disabled={true}
                      formControlStyles={{ marginTop: 28 }}
                    />
                    <FormItemTextField
                      name="phone"
                      type="tel"
                      disabled={true}
                      label={formatMessage({
                        id: 'aisOrder.phone',
                        defaultMessage: 'Telefonní číslo',
                      })}
                    />

                    {showGeneric && (
                      <>
                        <Typography component="span" variant="body1">
                          {formatMessage({ id: 'aisOrder.transferLinks' })}
                        </Typography>
                        <div>
                          <Button
                            className={classes.link}
                            onClick={() =>
                              newTabLink(`${UrlRoute.ARCHIVAL_COLLECTION}`)
                            }
                          >
                            <Typography component="span" variant="body2">
                              {formatMessage({
                                id: 'aisOrder.linkButtonCollections',
                              })}
                            </Typography>
                          </Button>
                          <Button
                            className={classes.link}
                            onClick={() =>
                              newTabLink(`${UrlRoute.ARCHIVAL_AID}`)
                            }
                          >
                            <Typography component="span" variant="body2">
                              {formatMessage({ id: 'aisOrder.linkButtonAids' })}
                            </Typography>
                          </Button>
                        </div>
                      </>
                    )}

                    {showAid && (
                      <>
                        <TextField
                          name="archivalCollection"
                          label="Archivní fond pomůcky"
                          value={`${values?.archivalAidMetadata?.archivalCollection?.evidenceNumber} - ${values?.archivalAidMetadata?.archivalCollection?.name}`}
                          onChange={NOOP}
                          disabled={true}
                          formControlStyles={{ marginTop: 28 }}
                        />
                        <TextField
                          name="archivalAid"
                          label="Archivní pomůcka"
                          value={`${values?.archivalAidMetadata?.evidenceNumber} - ${values?.archivalAidMetadata?.name}`}
                          onChange={NOOP}
                          disabled={true}
                          formControlStyles={{ marginTop: 28 }}
                        />
                        <Button
                          component="span"
                          variant="outlined"
                          color="primary"
                          className={classes.submitButton}
                          onClick={() =>
                            newTabLink(
                              `/pomucky/0/${values?.archivalAidMetadata?.id}`
                            )
                          }
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({
                              id: 'aisOrder.linkAidButton',
                            })}
                          </Typography>
                        </Button>
                        <Button
                          variant="contained"
                          className={classes.submitButton}
                          onClick={handleRemoveAisObject(props)}
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({ id: 'aisOrder.removeAidButton' })}
                          </Typography>
                        </Button>

                        <Typography component="p" variant="body1">
                          {formatMessage({ id: 'aisOrder.orderInfo' })}
                        </Typography>
                      </>
                    )}

                    {showCollection && (
                      <>
                        <TextField
                          name="archivalCollection"
                          label="Archivní fond"
                          value={`${values?.archivalCollection?.evidenceNumber} - ${values?.archivalCollection?.name}`}
                          onChange={NOOP}
                          disabled={true}
                          formControlStyles={{ marginTop: 28 }}
                        />
                        <Button
                          component="span"
                          variant="outlined"
                          color="primary"
                          className={classes.submitButton}
                          onClick={() =>
                            newTabLink(
                              `/fondy/0/${values?.archivalCollection?.id}`
                            )
                          }
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({
                              id: 'aisOrder.linkCollectionButton',
                            })}
                          </Typography>
                        </Button>
                        <Button
                          variant="contained"
                          className={classes.submitButton}
                          onClick={handleRemoveAisObject(props)}
                        >
                          <Typography component="span" variant="body2">
                            {formatMessage({
                              id: 'aisOrder.removeCollectionButton',
                            })}
                          </Typography>
                        </Button>

                        <Typography component="span" variant="body1">
                          {formatMessage({ id: 'aisOrder.orderInfo' })}
                        </Typography>
                      </>
                    )}

                    {showUnits && (
                      <List
                        component="div"
                        disablePadding
                        dense={true}
                        style={{
                          marginTop: 5,
                        }}
                        subheader={
                          <Typography component="h6" variant="h6">
                            Archiválie k objednání
                          </Typography>
                        }
                      >
                        {!!values?.archivalUnits?.length ? (
                          values?.archivalUnits?.map((unit) => (
                            <ListItem
                              key={unit.id}
                              className={classes.listItem}
                            >
                              <Button
                                className={classes.unitLink}
                                onClick={() =>
                                  newTabLink(
                                    archivalUnits?.find(
                                      (au) =>
                                        au.archivalUnitReference.id === unit.id
                                    )?.path ?? ''
                                  )
                                }
                              >
                                <Typography
                                  className={classes.listItemName}
                                  component="span"
                                >
                                  {unit?.numericId}
                                </Typography>
                                <IconButton
                                  size="small"
                                  className={classes.iconButton}
                                  onClick={handleRemoveItem(props, unit)}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </Button>
                            </ListItem>
                          ))
                        ) : (
                          <Typography component="span">
                            Žádné archivalie k objednání
                          </Typography>
                        )}
                      </List>
                    )}

                    <FormItemTextField
                      name="description"
                      label={formatMessage({
                        id: showGeneric
                          ? 'aisOrder.description'
                          : showUnits
                          ? 'aisOrder.descriptionUnits'
                          : 'aisOrder.descriptionAidAndCollection',
                      })}
                      multiline={true}
                    />
                    <FormItemTextField
                      name="requestedForDate"
                      type="date"
                      label={formatMessage({ id: 'aisOrder.requestedForDate' })}
                    />
                    <Typography component="span" variant="body1">
                      {formatMessage({ id: 'aisOrder.readyInfo' })}
                    </Typography>
                    <TextField
                      name="created"
                      type="date"
                      value={today}
                      label={formatMessage({ id: 'aisOrder.created' })}
                      onChange={NOOP}
                      disabled={true}
                      formControlStyles={{ marginTop: 28 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      className={classes.submitButton}
                      disabled={props.isSubmitting}
                    >
                      <Typography component="span" variant="body2">
                        {formatMessage({ id: 'aisOrder.submitButton' })}
                      </Typography>
                    </Button>
                  </form>
                );
              }}
            </Formik>
          </div>
        </Grid>
      ) : (
        <Grid item xs={10} md={6}>
          <Typography component="span" variant="body1" color="primary">
            {formatMessage({ id: 'aisOrder.withoutSheet' })}
          </Typography>
        </Grid>
      )}
      <OrderAcknowledgeDialog
        orderId={newOrderId}
        handleClose={() => setNewOrderId(undefined)}
      />
    </Grid>
  );
}
